import { useUser } from "@clerk/vue";
import { watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  identifyUser,
  resetAnalytics,
  trackEvent,
  setUserProperties,
  trackPageView,
} from "@/analytics";

// Define user type locally since it's not exported from @clerk/vue
interface UserResource {
  id: string;
  emailAddresses: Array<{
    emailAddress: string;
    verification?: { strategy?: string };
  }>;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  username: string | null;
  createdAt: number | string;
  imageUrl: string;
}

// Type guard to check if user is a UserResource (not just 'true')
function isUserResource(user: unknown): user is UserResource {
  return !!user && typeof user === "object" && "id" in user;
}

/**
 * Composable for integrating PostHog analytics with Clerk authentication
 * Handles user identification, event tracking, and page views
 */
export function useAnalytics() {
  // Get user data from Clerk
  const { user, isLoaded, isSignedIn } = useUser();
  let router: ReturnType<typeof useRouter> | null;

  try {
    router = useRouter();
  } catch {
    // Router might not be available in tests
    router = null;
  }

  // Watch for user authentication changes
  const setupUserIdentification = () => {
    watch(
      // Use getters for proper reactivity
      () => [isLoaded.value, isSignedIn.value, user.value],
      ([loaded, signedIn, currentUser]) => {
        if (!loaded) return;

        if (signedIn && currentUser && isUserResource(currentUser)) {
          // Identify user in PostHog
          identifyUser(currentUser.id, {
            email: currentUser.emailAddresses[0]?.emailAddress,
            firstName: currentUser.firstName || undefined,
            lastName: currentUser.lastName || undefined,
            fullName: currentUser.fullName || undefined,
            username: currentUser.username || undefined,
            createdAt: currentUser.createdAt,
            hasImage: Boolean(currentUser.imageUrl),
            // Add custom user properties
            clerkUserId: currentUser.id,
            authProvider: currentUser.emailAddresses[0]?.verification?.strategy,
          });

          // Determine if this is a new user (signed up recently)
          const userCreatedAt = currentUser.createdAt
            ? new Date(currentUser.createdAt)
            : null;
          const isNewUser =
            userCreatedAt && Date.now() - userCreatedAt.getTime() < 60000; // Within last minute

          if (isNewUser) {
            // Track sign up event for new users
            trackEvent("user_signed_up", {
              userId: currentUser.id,
              email: currentUser.emailAddresses[0]?.emailAddress,
              method:
                currentUser.emailAddresses[0]?.verification?.strategy ||
                "email",
              timestamp: new Date().toISOString(),
            });
          } else {
            // Track sign in event for returning users
            trackEvent("user_signed_in", {
              userId: currentUser.id,
              email: currentUser.emailAddresses[0]?.emailAddress,
              method: "clerk",
            });
          }
        } else if (!signedIn && loaded) {
          // User signed out, reset analytics
          resetAnalytics();
          trackEvent("user_signed_out");
        }
      },
      { immediate: true },
    );
  };

  // Track route changes for page views
  const setupPageTracking = () => {
    if (router) {
      router.afterEach((to, from) => {
        // Don't track if it's the same route
        if (to.path === from.path) return;

        trackPageView(to.fullPath);

        // Track navigation event with additional context
        trackEvent("page_viewed", {
          path: to.path,
          name: to.name,
          previousPath: from.path,
          previousName: from.name,
          query: to.query,
          params: to.params,
        });
      });
    }
  };

  // Initialize on mount
  onMounted(() => {
    setupUserIdentification();
    setupPageTracking();
  });

  // Return tracking functions for component use
  return {
    trackEvent,
    setUserProperties,
    trackSignUp: (method: string = "email") => {
      const currentUser = user.value;
      if (currentUser && isUserResource(currentUser)) {
        trackEvent("user_signed_up", {
          userId: currentUser.id,
          email: currentUser.emailAddresses[0]?.emailAddress,
          method,
          timestamp: new Date().toISOString(),
        });
      }
    },
    trackConversationStarted: (
      conversationId: string,
      metadata?: Record<string, unknown>,
    ) => {
      trackEvent("conversation_started", {
        conversationId,
        userId: isUserResource(user.value) ? user.value.id : undefined,
        ...metadata,
      });
    },
    trackMessageSent: (
      conversationId: string,
      messageType: string,
      metadata?: Record<string, unknown>,
    ) => {
      trackEvent("message_sent", {
        conversationId,
        messageType,
        userId: isUserResource(user.value) ? user.value.id : undefined,
        ...metadata,
      });
    },
    trackError: (error: Error, context?: Record<string, unknown>) => {
      trackEvent("error_occurred", {
        error: error.message,
        stack: error.stack,
        userId: isUserResource(user.value) ? user.value.id : undefined,
        ...context,
      });
    },
    trackFeatureUsed: (
      featureName: string,
      metadata?: Record<string, unknown>,
    ) => {
      trackEvent("feature_used", {
        feature: featureName,
        userId: isUserResource(user.value) ? user.value.id : undefined,
        ...metadata,
      });
    },
    trackPerformanceMetric: (
      metricName: string,
      value: number,
      metadata?: Record<string, unknown>,
    ) => {
      trackEvent("performance_metric", {
        metric: metricName,
        value,
        userId: isUserResource(user.value) ? user.value.id : undefined,
        ...metadata,
      });
    },
  };
}

/**
 * Track Core Web Vitals
 * Only enabled in production to avoid performance issues during development
 */
export function trackWebVitals() {
  // Skip in development to avoid performance overhead
  if (import.meta.env.MODE !== "production") {
    return;
  }

  // Track Largest Contentful Paint (LCP)
  if ("PerformanceObserver" in window) {
    try {
      let lcpReported = false;
      const lcpObserver = new PerformanceObserver((list) => {
        // Only report LCP once to avoid spamming
        if (lcpReported) return;
        lcpReported = true;

        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
          renderTime?: number;
          loadTime?: number;
          element?: Element;
        };
        trackEvent("web_vital_lcp", {
          value: lastEntry.renderTime || lastEntry.loadTime,
          element: lastEntry.element?.tagName,
        });
        lcpObserver.disconnect();
      });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
    } catch (e) {
      console.error("Failed to track LCP:", e);
    }

    // Track First Input Delay (FID)
    try {
      let fidReported = false;
      const fidObserver = new PerformanceObserver((list) => {
        // Only report FID once
        if (fidReported) return;
        fidReported = true;

        const entries = list.getEntries();
        entries.forEach((entry) => {
          const fidEntry = entry as PerformanceEntry & {
            processingStart?: number;
            startTime: number;
          };
          trackEvent("web_vital_fid", {
            value: (fidEntry.processingStart || 0) - fidEntry.startTime,
            eventType: fidEntry.name,
          });
        });
        fidObserver.disconnect();
      });
      fidObserver.observe({ type: "first-input", buffered: true });
    } catch (e) {
      console.error("Failed to track FID:", e);
    }

    // Track Cumulative Layout Shift (CLS)
    // Report only once after 5 seconds to get a stable value
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const clsEntry = entry as PerformanceEntry & {
            hadRecentInput?: boolean;
            value?: number;
          };
          if (!clsEntry.hadRecentInput) {
            clsValue += clsEntry.value || 0;
          }
        }
      });
      clsObserver.observe({ type: "layout-shift", buffered: true });

      // Report CLS after 5 seconds and disconnect
      setTimeout(() => {
        trackEvent("web_vital_cls", {
          value: clsValue,
        });
        clsObserver.disconnect();
      }, 5000);
    } catch (e) {
      console.error("Failed to track CLS:", e);
    }
  }
}
